import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { supabase, getCurrentUser } from '../lib/supabase';
import { useProfile } from '../lib/auth';
import { useDemo } from '../lib/demoContext';
import { useTheme } from '../lib/themeContext';
import AreteLogo from './AreteLogo';
import InfoTooltip from './InfoTooltip';

import { SidebarOnboarding } from './OnboardingChecklist';
import ReportButton from './ReportButton';
import FhirExportButton from './FhirExportButton';

/* ── Data source types ──────────────────────────────────────────────── */
interface DataSource {
  id: string;
  label: string;
  status: 'live' | 'disconnected' | 'checking';
  lastSync: string | null;
}

function StatusDot({ status }: { status: DataSource['status'] }) {
  if (status === 'checking')
    return <span className="w-2 h-2 rounded-full animate-pulse inline-block" style={{ background: 'rgba(27,48,34,0.12)' }} />;
  if (status === 'live')
    return <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block shadow-[0_0_4px_#4DC88A]" />;
  return <span className="w-2 h-2 rounded-full inline-block" style={{ background: 'rgba(27,48,34,0.12)' }} />;
}

/* ── Relative date label (for glance header) ───────────────────────── */
function relativeDate(dateStr: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const yest = new Date(now); yest.setDate(yest.getDate() - 1);
  const yestStr = `${yest.getFullYear()}-${pad(yest.getMonth() + 1)}-${pad(yest.getDate())}`;
  if (dateStr === todayStr)  return 'Today';
  if (dateStr === yestStr)   return 'Yesterday';
  const diff = Math.round((now.getTime() - new Date(dateStr + 'T12:00:00').getTime()) / 86400000);
  return `${diff}d ago`;
}

/* ── Short date+time label for data sources ────────────────────────── */
function shortDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
    + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/* ── Glance dot helper ──────────────────────────────────────────────── */
function glanceDot(
  val: number | null,
  lo: number,
  hi: number,
  labels?: { good: string; warn: string; mid: string },
): { dot: string; color: string; label: string } {
  if (val == null) return { dot: '◌', color: 'rgba(27,48,34,0.18)', label: '—' };
  const ok   = val >= lo && val <= hi;
  const warn = val < lo * 0.85 || val > hi * 1.15;
  const l = labels || { good: 'Good', warn: 'Low', mid: 'Watch' };
  if (ok)   return { dot: '●', color: '#4DC88A', label: l.good };
  if (warn) return { dot: '●', color: '#E57373', label: l.warn };
  return { dot: '●', color: '#F0A940', label: l.mid };
}

/* ── Glance metric item ─────────────────────────────────────────────── */
function GlanceItem({ label, value, info }: {
  label: string;
  value: string;
  info: { dot: string; color: string; label: string };
}) {
  return (
    <div>
      <div className="text-[10px]" style={{ color: 'rgba(27,48,34,0.58)' }}>{label}</div>
      <div className="text-lg font-semibold leading-tight" style={{ color: '#1B3022' }}>{value}</div>
      <div className="text-[11px]" style={{ color: info.color }}>
        {info.dot} {info.label}
      </div>
    </div>
  );
}

/* ── Section divider ──────────────────────────────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[9px] uppercase tracking-[0.16em] mb-2 px-1"
      style={{ color: 'rgba(27,48,34,0.62)', fontWeight: 600 }}
    >
      {children}
    </div>
  );
}

/* ── Main panel ─────────────────────────────────────────────────────── */
export default function ClientPanel() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const demo = useDemo();
  const { isDark, toggle: toggleTheme } = useTheme();

  /* Data sources — mirrors old Sidebar logic */
  const [sources, setSources] = useState<DataSource[]>([
    { id: 'oura',  label: 'Oura Ring',    status: 'checking', lastSync: null },
    { id: 'apple', label: 'Apple Health',  status: 'checking', lastSync: null },
    { id: 'labs',  label: 'Function Labs', status: 'checking', lastSync: null },
  ]);

  /* Today at a Glance — fetches latest metrics */
  const [glance, setGlance] = useState<{
    hrv: number | null; sleep: number | null; stress: number | null; steps: number | null;
    dataDate: string | null;
  }>({ hrv: null, sleep: null, stress: null, steps: null, dataDate: null });

  /* Demo mode — populate realistic sidebar data */
  useEffect(() => {
    if (!demo) return;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    const yStr = `${yesterday.getFullYear()}-${pad(yesterday.getMonth() + 1)}-${pad(yesterday.getDate())}`;
    setGlance({ hrv: 48, sleep: 7.2, stress: 28, steps: 9214, dataDate: yStr });
    setSources([
      { id: 'oura',  label: 'Oura Ring',    status: 'live', lastSync: 'Today 6:00 AM' },
      { id: 'apple', label: 'Apple Health',  status: 'live', lastSync: 'Today 7:15 AM' },
      { id: 'labs',  label: 'Function Labs', status: 'live', lastSync: 'Mar 1, 2026' },
    ]);
  }, [demo]);

  useEffect(() => {
    if (demo) return; // skip real fetches in demo mode
    async function loadPanelData() {
      // Reset to "checking" while we re-fetch
      setSources(s => s.map(src => ({ ...src, status: 'checking' as const })));

      const user = await getCurrentUser();
      if (!user) {
        setSources(s => s.map(src => ({ ...src, status: 'disconnected' as const })));
        setGlance({ hrv: null, sleep: null, stress: null, steps: null, dataDate: null });
        return;
      }

      // Data source checks
      try {
        const [profileRes, appleRes, labRes, ouraSyncRes] = await Promise.all([
          supabase.from('profiles').select('oura_token').eq('id', user.id).single(),
          supabase.from('apple_health_syncs').select('synced_at').eq('user_id', user.id)
            .order('synced_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('labs').select('created_at').eq('user_id', user.id)
            .order('created_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('oura_sync_log').select('last_synced_at').eq('user_id', user.id).maybeSingle(),
        ]);

        // Check both old (profiles.oura_token) and new (oura_tokens via sync_log) storage
        const hasOura  = !!profileRes.data?.oura_token || !!ouraSyncRes.data;
        const hasApple = !!appleRes.data;
        const hasLabs  = !!labRes.data;

        const appleSyncIso = appleRes.data?.synced_at ?? null;
        const labsSyncIso  = labRes.data?.created_at ?? null;
        const ouraSyncIso  = ouraSyncRes.data?.last_synced_at ?? null;

        setSources([
          { id: 'oura',  label: 'Oura Ring',    status: hasOura  ? 'live' : 'disconnected', lastSync: ouraSyncIso ? shortDateTime(ouraSyncIso) : null },
          { id: 'apple', label: 'Apple Health',  status: hasApple ? 'live' : 'disconnected', lastSync: appleSyncIso ? shortDateTime(appleSyncIso) : null },
          { id: 'labs',  label: 'Function Labs', status: hasLabs  ? 'live' : 'disconnected', lastSync: labsSyncIso  ? shortDateTime(labsSyncIso)  : null },
        ]);
      } catch (e) {
        console.warn('[ClientPanel] data source check error:', e);
        setSources(s => s.map(src => ({ ...src, status: 'disconnected' as const })));
      }

      // Fetch latest metrics for "Today at a Glance" from daily_health_summary
      {
        try {
          const now = new Date();
          const fmt = (d: Date) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const localToday = fmt(now);

          const { data: summaryRows } = await supabase
            .from('daily_health_summary')
            .select('date, sleep_score, hrv_rmssd, session_hrv, daytime_stress, steps, total_sleep_mins')
            .eq('user_id', user.id)
            .lt('date', localToday)
            .order('date', { ascending: false })
            .limit(3);

          if (summaryRows && summaryRows.length > 0) {
            const row = summaryRows[0];
            const hrvVal = row.hrv_rmssd ?? row.session_hrv ?? null;
            const sleepHours = row.total_sleep_mins != null
              ? Math.round((row.total_sleep_mins / 60) * 10) / 10
              : null;
            const stressVal = row.daytime_stress ?? null;

            // Also check Apple Health steps for this date
            const { data: appleSteps } = await supabase
              .from('health_metrics')
              .select('value')
              .eq('user_id', user.id)
              .eq('source', 'apple_health')
              .ilike('metric_name', '%step%')
              .eq('date', row.date)
              .limit(1)
              .maybeSingle();

            setGlance({
              hrv:    hrvVal != null ? Math.round(hrvVal) : null,
              sleep:  sleepHours,
              stress: stressVal,
              steps:  appleSteps?.value != null ? Math.round(appleSteps.value) : (row.steps ?? null),
              dataDate: row.date,
            });
          }
        } catch (e) {
          console.warn('[ClientPanel] glance fetch error:', e);
        }
      }
    }

    loadPanelData();

    // Re-fetch everything when user logs in/out so devices & glance data persist
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        loadPanelData();
      } else {
        setSources(s => s.map(src => ({ ...src, status: 'disconnected' as const })));
        setGlance({ hrv: null, sleep: null, stress: null, steps: null, dataDate: null });
      }
    });

    // Re-fetch when data sources change (Connect page dispatches this)
    const handleSourceChange = () => loadPanelData();
    window.addEventListener('arete:datasource-change', handleSourceChange);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('arete:datasource-change', handleSourceChange);
    };
  }, [demo]); // eslint-disable-line react-hooks/exhaustive-deps

  const hrvInfo    = glanceDot(glance.hrv,    40, 80);
  const sleepInfo  = glanceDot(glance.sleep,  7, 9);
  const stressInfo = glanceDot(glance.stress, 60, 100, { good: 'Resilient', warn: 'Elevated', mid: 'Moderate' });
  const stepsInfo  = (() => {
    if (glance.steps == null) return { dot: '◌', color: 'rgba(27,48,34,0.18)', label: '—' };
    if (glance.steps >= 8000) return { dot: '●', color: '#4DC88A', label: 'Target met' };
    if (glance.steps >= 5000) return { dot: '●', color: '#F0A940', label: 'Getting there' };
    return { dot: '●', color: '#E57373', label: 'Below target' };
  })();

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  return (
    <aside
      className="hidden lg:flex flex-col w-60 py-7 px-5"
      style={{
        margin: '24px 0 24px 24px',
        borderRadius: '2rem',
        background: 'rgba(245,248,242,0.99)',
        backdropFilter: 'blur(20px) saturate(1.1)',
        WebkitBackdropFilter: 'blur(20px) saturate(1.1)',
        border: '1px solid rgba(214,224,207,0.55)',
        boxShadow: '0 8px 32px rgba(47,74,52,0.08), inset 0 1px 0 rgba(255,255,255,0.5)',
        minHeight: 'calc(100vh - 48px)',
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 24,
      }}
    >
      {/* Logo */}
      <div className="mb-7 px-1">
        <AreteLogo iconSize={28} fontSize="1rem" onDark={false} />
      </div>

      {/* Today at a Glance */}
      <div className="mb-5">
        <div className="flex items-center gap-1">
          <SectionLabel>Prior Day</SectionLabel>
          <InfoTooltip text="Single-day values from your most recent complete day. Dashboard pillar scores use a multi-day rolling average instead." ariaLabel="Prior Day info" />
        </div>
        <div
          className="rounded-2xl px-4 py-3.5"
          style={{ background: 'rgba(214,224,207,0.35)', border: '1px solid rgba(214,224,207,0.40)' }}
        >
          <div className="flex items-baseline justify-end mb-2">
            {glance.dataDate && (
              <span
                className="text-[9px]"
                style={{ color: relativeDate(glance.dataDate) === 'Yesterday' ? 'rgba(47,74,52,0.30)' : 'rgba(180,120,40,0.75)' }}
              >
                {relativeDate(glance.dataDate)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <GlanceItem label="HRV"     value={glance.hrv != null ? `${glance.hrv} ms` : '—'}     info={hrvInfo} />
            <GlanceItem label="Sleep"   value={glance.sleep != null ? `${glance.sleep} hrs` : '—'} info={sleepInfo} />
            <GlanceItem label="Resilience"  value={glance.stress != null ? `${glance.stress}/100` : '—'} info={stressInfo} />
            <GlanceItem label="Steps"   value={glance.steps != null ? glance.steps.toLocaleString() : '—'} info={stepsInfo} />
          </div>
        </div>
      </div>

      {/* Patient Info */}
      <div className="mb-5">
        <SectionLabel>Patient</SectionLabel>
        <div
          className="rounded-2xl px-4 py-3.5"
          style={{ background: 'rgba(214,224,207,0.35)', border: '1px solid rgba(214,224,207,0.40)' }}
        >
          <div className="text-sm font-semibold" style={{ color: '#1B3022' }}>
            {profile?.full_name || 'Patient'}
          </div>
          <div className="text-xs leading-relaxed mt-1" style={{ color: 'rgba(27,48,34,0.68)' }}>
            {profile?.dob ? `DOB ${new Date(profile.dob + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}` : ''}
            {profile?.role === 'admin' && (
              <span className="ml-1.5" style={{ color: '#9B8FD4' }}>Admin</span>
            )}
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <SidebarOnboarding />

      {/* Data Sources */}
      <div className="mb-5 flex-1">
        <div className="flex items-center justify-between px-1 mb-2">
          <SectionLabel>Data Sources</SectionLabel>
          <NavLink
            to="/connect"
            className="text-[10px] font-medium"
            style={{ color: '#6F8A5A', transition: 'color 0.3s ease' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#5A7548'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#6F8A5A'; }}
          >
            + Add
          </NavLink>
        </div>
        <div className="space-y-0.5">
          {sources.map(src => (
            <NavLink
              key={src.id}
              to="/connect"
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl group"
              style={{ background: 'transparent', transition: 'all 0.3s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(214,224,207,0.30)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium" style={{ color: '#2F4A34', transition: 'color 0.3s ease' }}>
                  {src.label}
                </span>
                <div className="text-[10px]" style={{ color: 'rgba(27,48,34,0.55)' }}>
                  {src.status === 'checking' ? '…' : src.status === 'live' ? src.lastSync || 'Connected' : 'Not connected'}
                </div>
              </div>
              <StatusDot status={src.status} />
            </NavLink>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="mb-3 px-1">
        <NavLink
          to="/labs"
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium"
          style={({ isActive }) => ({
            color: isActive ? '#1B3022' : 'rgba(27,48,34,0.60)',
            background: isActive ? 'rgba(214,224,207,0.30)' : 'transparent',
            transition: 'all 0.3s ease',
          })}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(214,224,207,0.30)'; }}
          onMouseLeave={e => {
            const isAct = e.currentTarget.getAttribute('aria-current') === 'page';
            if (!isAct) e.currentTarget.style.background = 'transparent';
          }}
        >
          🧪 Lab Results
        </NavLink>
      </div>

      {/* Export */}
      <div className="mb-3 px-1 space-y-1">
        <ReportButton compact />
        <FhirExportButton compact />
      </div>

      {/* How We Score */}
      <a href="/methodology" className="flex items-center gap-2 text-xs text-arete-muted dark:text-gray-400 hover:text-arete-forest dark:hover:text-gray-200 px-1 mb-3 transition-colors">
        <span>📊</span> How We Score
      </a>

      {/* Dark mode toggle */}
      <button
        onClick={toggleTheme}
        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium mb-1"
        style={{ color: 'rgba(27,48,34,0.60)', transition: 'all 0.3s ease' }}
        onMouseEnter={e => { e.currentTarget.style.color = '#1B3022'; e.currentTarget.style.background = 'rgba(214,224,207,0.30)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(27,48,34,0.60)'; e.currentTarget.style.background = 'transparent'; }}
      >
        <span>{isDark ? '☀' : '☽'}</span>
        <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
      </button>

      {/* Sign out */}
      <button
        onClick={handleSignOut}
        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium"
        style={{ color: 'rgba(27,48,34,0.60)', transition: 'all 0.3s ease' }}
        onMouseEnter={e => { e.currentTarget.style.color = '#1B3022'; e.currentTarget.style.background = 'rgba(214,224,207,0.30)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(27,48,34,0.60)'; e.currentTarget.style.background = 'transparent'; }}
      >
        <span>↩</span>
        <span>Sign Out</span>
      </button>

    </aside>
  );
}
